/**
 * Code and QR payload handling.
 *
 * The QR payload is a short versioned string, deliberately NOT a URL: these labels get
 * printed and physically stuck onto ~200 carts, and a URL would bake a hostname into every
 * one of them. The day the domain changes, the whole fleet would need relabelling.
 *
 * Format:  v1:CHT-000123   v1:SITE-000004
 *
 * The version prefix exists so that a label produced by a newer app version fails with
 * "update the app" instead of the indistinguishable "unknown code".
 */

export const QR_VERSION = 1 as const;

export type EntityKind = 'cart' | 'site';

const PREFIX: Record<EntityKind, string> = { cart: 'CHT', site: 'SITE' };

/** `CHT-000123` / `SITE-000004` — 6 digits, zero padded. */
export function formatCode(kind: EntityKind, serial: number): string {
  return `${PREFIX[kind]}-${String(serial).padStart(6, '0')}`;
}

/** The string encoded into the printed QR image. */
export function formatQrPayload(code: string): string {
  return `v${QR_VERSION}:${code}`;
}

export type ParsedQr =
  | { ok: true; kind: EntityKind; code: string; serial: number }
  | { ok: false; reason: 'malformed' | 'unknown_version' | 'legacy_unversioned'; detail?: string };

const VERSIONED = /^v(\d+):(SITE|CHT)-(\d{6})$/;
const BARE = /^(SITE|CHT)-(\d{6})$/;

/**
 * Parse a scanned payload. Every failure mode is distinguishable, because the operator
 * standing in a warehouse needs to know whether the label is wrong, the app is stale, or
 * the code simply isn't in their local catalogue (the last of which is not decided here).
 */
export function parseQrPayload(raw: string): ParsedQr {
  const s = raw.trim();

  const m = VERSIONED.exec(s);
  if (m) {
    const version = Number(m[1]);
    if (version !== QR_VERSION) {
      return { ok: false, reason: 'unknown_version', detail: `v${version}` };
    }
    const kind: EntityKind = m[2] === 'CHT' ? 'cart' : 'site';
    return { ok: true, kind, code: `${m[2]}-${m[3]}`, serial: Number(m[3]) };
  }

  // Labels from the two prototypes carried the bare code with no version prefix. Those
  // never reached production, but a stray test label is cheap to diagnose properly.
  if (BARE.test(s)) return { ok: false, reason: 'legacy_unversioned', detail: s };

  return { ok: false, reason: 'malformed', detail: s.slice(0, 40) };
}

/**
 * Normalise something a human typed into a scannable payload.
 *
 * Someone reading a torn label sees `CHT-000123`, not `v1:CHT-000123`, and a phone keyboard
 * may or may not have capitalised it. Everything else is passed through untouched so that a
 * genuinely unreadable input still reaches `parseQrPayload` and gets a precise diagnosis
 * rather than being silently mangled into something else.
 */
export function normaliseScanInput(raw: string): string {
  const trimmed = raw.trim();

  const bare = /^(site|cht)-(\d{6})$/i.exec(trimmed);
  if (bare) return `v${QR_VERSION}:${bare[1]!.toUpperCase()}-${bare[2]}`;

  // The version number is carried through rather than forced to the current one: rewriting
  // a v2 label as v1 would turn "update the app" into a bogus "unknown code".
  const versioned = /^v(\d+):(site|cht)-(\d{6})$/i.exec(trimmed);
  if (versioned) return `v${versioned[1]}:${versioned[2]!.toUpperCase()}-${versioned[3]}`;

  return trimmed;
}
